import { signal, computed, effect } from '@preact/signals';
import { DEFAULT_SETTINGS, type UserDto, type UserSettings } from '../../shared/types';
import { api } from '../api';
import { setPlaybackRate } from './player';

export const user = signal<UserDto | null>(null);
export const sessionLoading = signal(true);
export const isSignedIn = computed(() => user.value !== null);

export const settings = computed<UserSettings>(() => ({
  ...DEFAULT_SETTINGS,
  ...(user.value?.settings ?? {}),
}));

export async function loadSession(): Promise<void> {
  try {
    const { user: me } = await api.me();
    user.value = me;
    if (me?.settings.defaultPlaybackRate) setPlaybackRate(me.settings.defaultPlaybackRate);
  } catch {
    user.value = null;
  } finally {
    sessionLoading.value = false;
  }
}

export async function signOut(): Promise<void> {
  await api.logout().catch(() => {});
  await endSession();
}

/**
 * Adopt a freshly authenticated user.
 *
 * Every sign-in flow goes through here rather than assigning `user` directly, so that none of
 * them can forget the cache. Whoever was here before is not necessarily the person signing in
 * now, and anything the previous session left behind is theirs, not this user's.
 */
export async function beginSession(u: UserDto): Promise<void> {
  await clearCachedResponses();
  user.value = u;
  if (u.settings.defaultPlaybackRate) setPlaybackRate(u.settings.defaultPlaybackRate);
}

/** Sign-out and account deletion both end a session; only how they got there differs. */
export async function endSession(): Promise<void> {
  user.value = null;
  await clearCachedResponses();
}

/**
 * Drop anything the service worker cached for the session that is ending or beginning.
 *
 * The worker strips per-user fields before caching and never caches a wholly personal
 * response, but a cache written by an older version can still hold them, and they outlive the
 * session that produced them — offline, the next person to open the app on a shared device
 * would be served the previous user's library.
 *
 * Both routes are used deliberately: the message reaches the active worker, and deleting the
 * caches directly covers the case where no worker is controlling this page yet.
 */
async function clearCachedResponses(): Promise<void> {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'clear-runtime-cache' });
  } catch {
    // Nothing controlling the page; the direct delete below still applies.
  }

  try {
    if (typeof caches === 'undefined') return;
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.includes('runtime')).map((k) => caches.delete(k)));
  } catch {
    // Cache Storage is unavailable in some private modes; sign-out must still complete.
  }
}

export async function saveSettings(patch: UserSettings): Promise<void> {
  const { user: updated } = await api.updateSettings(patch);
  user.value = updated;
}

// ----------------------------------------------------------------------------- theme

export const systemPrefersDark = signal(
  typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)').matches : false,
);

if (typeof matchMedia === 'function') {
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    systemPrefersDark.value = e.matches;
  });
}

/** Reflect the resolved theme onto the root element so CSS can key off one attribute. */
effect(() => {
  const choice = settings.value.theme ?? 'system';
  const dark = choice === 'dark' || (choice === 'system' && systemPrefersDark.value);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  }
});
